import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Download, Star, UtensilsCrossed } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Panel, WaButton, itDate } from "@/components/kit";
import { api, errMsg } from "@/lib/api";

const copy = (u) => navigator.clipboard.writeText(u).then(() => toast.success("Link copiato"));

function LinkRow({ url, testId }) {
  return <div className="flex gap-2"><input readOnly value={url} className="flex-1 min-w-0 text-xs rounded-md border border-[#D4E6EC] px-3 py-2 bg-[#F5FAFB]" data-testid={`${testId}-url`} /><Button size="icon" variant="outline" onClick={() => copy(url)} data-testid={`${testId}-copy`}><Copy size={14} /></Button></div>;
}

function RsvpBlock({ c, url }) {
  const [r, setR] = useState(null);
  const load = useCallback(() => api.get(`/contracts/${c.id}/rsvp`).then((x) => setR(x.data)), [c.id]);
  useEffect(() => {
    load();
  }, [load]);
  const imp = async () => {
    try {
      const x = await api.post(`/contracts/${c.id}/rsvp/import`);
      toast.success(`${x.data.added} ospiti aggiunti alla lista`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const d = c.data || {};
  return (
    <Panel title="Conferme degli invitati" testId="rsvp-panel">
      <p className="text-sm text-[#47606B] -mt-2">Gli sposi / il festeggiato girano questo link ai loro invitati: ognuno conferma presenza e allergie, e voi le importate nella lista ospiti.</p>
      <LinkRow url={url} testId="rsvp-link" />
      <WaButton phone={d.cliente_tel} testId="rsvp-wa" label="Invia il link al cliente" text={`💌 Gentile *${d.cliente_nome}*,\n\nper raccogliere le conferme dei vostri invitati abbiamo preparato una pagina dedicata: basta girare questo link nei vostri gruppi 👇\n${url}\n\n🍽️ Ognuno indicherà presenza, bambini e allergie, e noi aggiorneremo la lista ospiti.\n\nUn cordiale saluto 🌊`} />
      {r && <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 text-center text-sm" data-testid="rsvp-totals">{[["Risposte", r.totali.risposte], ["Ci saranno", r.totali.si], ["Assenti", r.totali.no], ["Adulti", r.totali.adulti], ["Bambini", r.totali.bambini]].map(([l, v]) => <div key={l} className="rounded-lg bg-[#EAF5F7] p-2"><div className="font-serif-it text-2xl">{v}</div><div className="text-[11px] text-[#7B929C]">{l}</div></div>)}</div>}
      {r?.rows.length > 0 && (
        <div className="max-h-64 overflow-y-auto text-sm divide-y divide-[#EAF5F7]">{r.rows.map((x) => <div key={x.nome} className="py-1.5 flex justify-between gap-2"><span>{x.presenza === "si" ? "✅" : "❌"} {x.nome}{x.accompagnatori && <span className="text-[#7B929C]"> + {x.accompagnatori}</span>}</span><span className="text-xs text-[#7B929C] text-right">{x.presenza === "si" && `${x.adulti} ad. ${x.bambini ? `• ${x.bambini} bimbi` : ""}`}{x.allergie && <span className="text-[#B45309]"> • {x.allergie}</span>}{x.imported && " • in lista"}</span></div>)}</div>
      )}
      <Button size="sm" className="rounded-full" onClick={imp} disabled={!r?.rows.some((x) => x.presenza === "si" && !x.imported)} data-testid="rsvp-import"><Download size={14} className="mr-1" /> Importa nella lista ospiti</Button>
    </Panel>
  );
}

export function ExperiencePanel({ c }) {
  const nav = useNavigate();
  const [x, setX] = useState(null);
  useEffect(() => {
    Promise.all([api.post(`/contracts/${c.id}/links`), api.get(`/contracts/${c.id}/experience`)]).then(([l, e]) => setX({ ...e.data, links: l.data })).catch((e) => toast.error(errMsg(e)));
  }, [c.id]);
  if (!x) return <div className="text-sm text-[#7B929C]">Caricamento…</div>;
  const o = window.location.origin, d = c.data || {};
  const surveyUrl = `${o}/questionario/${x.links.survey}`;
  const s = x.survey;
  const tasted = (x.tasting?.dishes || []).filter((t) => t.scelto !== null && t.scelto !== undefined).length;
  return (
    <div className="space-y-6">
      <Panel title="Prova menù" testId="tasting-panel" right={<Button className="rounded-full" onClick={() => nav(`/contratti/${c.id}/degustazione`)} data-testid="open-tasting"><UtensilsCrossed size={15} className="mr-2" /> Apri sul tablet</Button>}>
        <p className="text-sm text-[#47606B] -mt-2">Durante la degustazione gli sposi scelgono i piatti del menù (Allegato A) con un tocco, danno un voto e lasciano note per la cucina. Con un clic le scelte aggiornano il menù del contratto.</p>
        {x.tasting && <div className="text-sm" data-testid="tasting-summary">Ultima degustazione: {itDate(x.tasting.at)} • {tasted} piatti valutati</div>}
      </Panel>
      <RsvpBlock c={c} url={`${o}/conferma/${x.links.rsvp}`} />
      <Panel title="Dopo l'evento" testId="survey-panel">
        <p className="text-sm text-[#47606B] -mt-2">1-4 giorni dopo l'evento il promemoria compare in «Oggi». Chi dà un voto medio da 4 in su vede subito i pulsanti per recensirvi e riceve un codice amico.</p>
        <LinkRow url={surveyUrl} testId="survey-link" />
        <WaButton phone={d.cliente_tel} testId="survey-wa" label="Chiedi il parere" text={`💙 Gentile *${d.cliente_nome}*,\n\nè stato un onore vivere con voi questo giorno! ✨ Ci aiutereste con 1 minuto del vostro tempo? 👉 ${surveyUrl}\n\nGrazie di cuore 🌊`} />
        {s ? (
          <div className="rounded-lg bg-[#EAF5F7] p-4 text-sm space-y-1" data-testid="survey-result">
            <div className="font-serif-it text-2xl flex items-center gap-1"><Star size={18} className="text-[#E3B04B] fill-[#E3B04B]" /> {s.media}/5</div>
            <div className="text-xs text-[#47606B]">Cucina {s.voti.cibo} • Servizio {s.voti.servizio} • Location {s.voti.location} • Organizzazione {s.voti.organizzazione}{s.consiglierebbe && " • Ci consiglierebbe"}</div>
            {s.commento && <p className="italic">“{s.commento}”</p>}
          </div>
        ) : <div className="text-sm text-[#7B929C]">Questionario non ancora compilato.</div>}
        {x.referral_code && <div className="text-sm" data-testid="referral-info">Codice amico del cliente: <b>{x.referral_code}</b>{x.referrals.length > 0 && <> • ha portato: {x.referrals.map((r) => `${r.nome} (${r.stage})`).join(", ")}</>}</div>}
      </Panel>
    </div>
  );
}
